import {
  Component,
  Output,
  EventEmitter,
  Input,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreService } from 'src/app/services/core.service';
import { MatDialog } from '@angular/material/dialog';
import { navItems } from '../sidebar/sidebar-data';
import { TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { FeatherModule } from 'angular-feather';
import { CognitoServiceService } from 'src/app/services/auth/cognito-service.service';
import { CompaniesService, Company } from 'src/app/services/companies.service';
import { UserPermissionsService } from 'src/app/services/user-permissions.service';
import { UserCompany } from 'src/app/interfaces/permission.interface';
import { UserInfoService } from 'src/app/services/user-info.service';


interface notifications {
  id: number;
  img: string;
  title: string;
  subtitle: string;
}

interface profiledd {
  id: number;
  img: string;
  title: string;
  subtitle: string;
  link: string;
}

interface apps {
  id: number;
  img: string;
  title: string;
  subtitle: string;
  link: string;
}

interface quicklinks {
  id: number;
  title: string;
  link: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, CommonModule, NgScrollbarModule, TablerIconsModule, MaterialModule, FeatherModule],
  templateUrl: './header.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  showFiller = false;

  // Companies dropdown
  companies: Company[] = [];
  selectedCompany: Company | null = null;
  loadingCompanies = false;
  changingCompany = false;  // Loading al cambiar de compañía
  targetCompanyName = '';   // Nombre de la compañía destino para mostrar en el overlay
  changeProgress = 0;       // Progress bar 0-100
  changeComplete = false;   // Mostrar check antes del reload

  // User info
  userName: string = 'Usuario';
  userEmail: string = '';
  userPicture: string = '/assets/images/profile/user-1.jpg';

  public selectedLanguage: any = {
    language: 'English',
    code: 'en',
    type: 'US',
    icon: '/assets/images/flag/icon-flag-en.svg',
  };

  public languages: any[] = [
    {
      language: 'English',
      code: 'en',
      type: 'US',
      icon: '/assets/images/flag/icon-flag-en.svg',
    },
    {
      language: 'Español',
      code: 'es',
      icon: '/assets/images/flag/icon-flag-es.svg',
    },
    {
      language: 'Français',
      code: 'fr',
      icon: '/assets/images/flag/icon-flag-fr.svg',
    },
    {
      language: 'German',
      code: 'de',
      icon: '/assets/images/flag/icon-flag-de.svg',
    },
  ];

  constructor(
    private vsidenav: CoreService,
    public dialog: MatDialog,
    private authService: CognitoServiceService,
    private translate: TranslateService,
    private companiesService: CompaniesService,
    private userPermissionsService: UserPermissionsService,
    private userInfoService: UserInfoService
  ) {
    translate.setDefaultLang('en');
  }

  ngOnInit() {
    // IMPORTANTE: Cargar la compañía seleccionada ANTES de suscribirse a userCompanies$
    // porque BehaviorSubject emite inmediatamente y podría sobrescribir la selección
    this.selectedCompany = this.companiesService.getSelectedCompany();
    
    this.loadCompanies();
    this.loadUserInfo();
  }

  loadUserInfo() {
    // Suscribirse a cambios en la información del usuario
    this.userInfoService.userInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(userInfo => {
        if (userInfo) {
          this.userName = this.userInfoService.getFullName();
          this.userEmail = this.userInfoService.getEmail();
          this.userPicture = this.userInfoService.getProfilePicture();
        }
      });
  }

  loadCompanies() {
    this.loadingCompanies = true;

    // Cargar solo compañías autorizadas del usuario desde permisos
    this.userPermissionsService.userCompanies$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (userCompanies) => {
          if (userCompanies && userCompanies.length > 0) {
            // Convertir UserCompany[] a Company[]
            this.companies = userCompanies.map(uc => ({
              IdCompany: uc.IdCompany,
              Name: uc.NameCompany,  // Usar NameCompany (nombre de la compañía)
              BusinessName: uc.BusinessName || uc.NameCompany
            }));

            // Validar que la compañía guardada esté en las autorizadas
            if (this.selectedCompany) {
              const isAuthorized = this.companies.some(c => c.IdCompany === this.selectedCompany?.IdCompany);
              if (!isAuthorized) {
                this.companiesService.clearSelectedCompany();
                this.selectedCompany = null;
              }
            }

            // Si no hay compañía seleccionada, usar la por defecto (sin reload)
            if (!this.selectedCompany) {
              const defaultCompany = this.userPermissionsService.getDefaultCompany();
              if (defaultCompany) {
                const companyToSelect = this.companies.find(c => c.IdCompany === defaultCompany.IdCompany);
                if (companyToSelect) {
                  // Solo setear sin reload para evitar ciclo infinito
                  this.selectedCompany = companyToSelect;
                  this.companiesService.setSelectedCompany(companyToSelect);
                }
              } else if (this.companies.length > 0) {
                // Fallback: seleccionar la primera (sin reload)
                this.selectedCompany = this.companies[0];
                this.companiesService.setSelectedCompany(this.companies[0]);
              }
            }
          } else {
            this.companies = [];
          }
          this.loadingCompanies = false;
        },
        error: (error) => {
          console.error('Error loading authorized companies:', error);
          this.loadingCompanies = false;
        }
      });
  }

  selectCompany(company: Company) {
    // Evitar cambio a la misma compañía
    if (this.selectedCompany?.IdCompany === company.IdCompany) {
      return;
    }
    
    // Iniciar el proceso de cambio con feedback visual
    this.changingCompany = true;
    this.targetCompanyName = company.Name;
    this.changeProgress = 0;
    this.changeComplete = false;
    this.selectedCompany = company;
    
    // Guardar en localStorage ANTES del reload
    this.companiesService.setSelectedCompany(company);

    // Animar el progress bar
    this.animateProgress();
  }

  private animateProgress() {
    const duration = 800; // ms totales
    const steps = 20;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      this.changeProgress = Math.min((currentStep / steps) * 100, 100);

      if (currentStep >= steps) {
        clearInterval(interval);
        // Mostrar check de completado brevemente
        this.changeComplete = true;
        
        setTimeout(() => {
          // Recargar la página
          window.location.reload();
        }, 400);
      }
    }, stepDuration);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openDialog() {
    const dialogRef = this.dialog.open(AppSearchDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      console.log(`Dialog result: ${result}`);
    });
  }

  changeLanguage(lang: any): void {
    this.translate.use(lang.code);
    this.selectedLanguage = lang;
  }

  notifications: notifications[] = [
    {
      id: 1,
      img: '/assets/images/profile/user-1.jpg',
      title: 'Roman Joined the Team!',
      subtitle: 'Congratulate him',
    },
    {
      id: 2,
      img: '/assets/images/profile/user-2.jpg',
      title: 'New message received',
      subtitle: 'Salma sent you new message',
    },
    {
      id: 3,
      img: '/assets/images/profile/user-3.jpg',
      title: 'New Payment received',
      subtitle: 'Check your earnings',
    },
    {
      id: 4,
      img: '/assets/images/profile/user-4.jpg',
      title: 'Jolly completed tasks',
      subtitle: 'Assign her new tasks',
    },
    {
      id: 5,
      img: '/assets/images/profile/user-5.jpg',
      title: 'Roman Joined the Team!',
      subtitle: 'Congratulate him',
    },
  ];

  profiledd: profiledd[] = [
    {
      id: 1,
      img: '/assets/images/svgs/icon-account.svg',
      title: 'My Profile',
      subtitle: 'Account Settings',
      link: '/',
    },
    {
      id: 2,
      img: '/assets/images/svgs/icon-inbox.svg',
      title: 'My Inbox',
      subtitle: 'Messages & Email',
      link: '/apps/email/inbox',
    },
    {
      id: 3,
      img: '/assets/images/svgs/icon-tasks.svg',
      title: 'My Tasks',
      subtitle: 'To-do and Daily Tasks',
      link: '/apps/taskboard',
    },
  ];

  // Método para cerrar sesión
  logout() {
    this.authService.logOut();
  }

}

@Component({
  selector: 'search-dialog',
  standalone: true,
  imports: [
    RouterModule,
    MaterialModule,
    TablerIconsModule,
    FormsModule
  ],
  templateUrl: 'search-dialog.component.html',
})
export class AppSearchDialogComponent {
  searchText: string = '';
  navItems = navItems;

  navItemsData = navItems.filter((navitem) => navitem.displayName);

  // filtered = this.navItemsData.find((obj) => {
  //   return obj.displayName == this.searchinput;
  // });
}
